/**
 * Смена пароля по ссылке из письма.
 *
 * Открывают страницу нередко в другом браузере, где сессии нет, поэтому метод
 * публичный и вход не требуется. После успеха сервер закрывает все прежние
 * сессии — войти нужно заново, уже с новым паролем.
 */
import { zodResolver } from "@hookform/resolvers/zod";
import type { TFunction } from "i18next";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { Link, useSearchParams } from "react-router-dom";
import { z } from "zod";

import { useAuthSettings, useResetPassword } from "../api/queries";
import { AuthShell, Field, FormError } from "../components/authForm";
import { errorMessage } from "../lib/authForm";
import { PasswordStrength } from "../components/PasswordStrength";
import type { Strength } from "../lib/passwordStrength";

const schema = (t: TFunction) =>
  z
    .object({
      password: z.string().min(8, t("auth.error.passwordShort")),
      passwordConfirm: z.string().min(1, t("auth.error.passwordRepeat")),
    })
    .refine((values) => values.password === values.passwordConfirm, {
      message: t("auth.error.passwordMismatch"),
      path: ["passwordConfirm"],
    });

type Values = z.infer<ReturnType<typeof schema>>;

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const settings = useAuthSettings();
  const reset = useResetPassword();
  const [serverError, setServerError] = useState<string | null>(null);
  const [strength, setStrength] = useState<Strength | null>(null);
  const [done, setDone] = useState(false);

  // Несовпадение показывается по потере фокуса, а не после отправки: так же,
  // как в регистрации, где кнопку до капчи вообще не нажать.
  const { t } = useTranslation();
  const form = useForm<Values>({
    resolver: zodResolver(useMemo(() => schema(t), [t])),
    mode: "onTouched",
  });
  const password = form.watch("password") ?? "";

  const minScore = settings.data?.passwordMinScore ?? 2;
  const weakPassword = password.length > 0 && strength !== null && !strength.acceptable;

  if (!token) {
    return (
      <AuthShell
        title={t("reset.broken.title")}
        subtitle={t("reset.broken.subtitle")}
        footer={
          <Link to="/forgot-password" className="text-sm text-amber-300 hover:text-amber-200">
            {t("reset.requestNew")}
          </Link>
        }
      >
        <p className="text-sm text-ink-400">
          {t("reset.broken.hint")}
        </p>
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell
        title={t("reset.done.title")}
        subtitle={t("reset.done.subtitle")}
        footer={
          <Link to="/login" className="btn-primary">
            {t("nav.login")}
          </Link>
        }
      >
        <p className="text-sm text-ink-400">
          {t("reset.done.hint")}
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title={t("reset.title")}
      subtitle={t("reset.subtitle")}
      footer={
        <p className="text-sm text-ink-400">
          {t("reset.expired")}{" "}
          <Link to="/forgot-password" className="text-amber-300 hover:text-amber-200">
            {t("reset.requestNewShort")}
          </Link>
        </p>
      }
    >
      <form
        className="space-y-4"
        onSubmit={form.handleSubmit((values) => {
          setServerError(null);

          reset.mutate(
            { token, newPassword: values.password },
            {
              onSuccess: () => setDone(true),
              onError: (error) => setServerError(errorMessage(error)),
            },
          );
        })}
      >
        <Field label={t("auth.field.password")} error={form.formState.errors.password?.message}>
          <input
            type="password"
            className="field"
            autoComplete="new-password"
            autoFocus
            {...form.register("password")}
          />
          <PasswordStrength
            password={password}
            userInputs={[]}
            minScore={minScore}
            onChange={setStrength}
          />
        </Field>

        <Field label={t("auth.field.passwordConfirm")} error={form.formState.errors.passwordConfirm?.message}>
          <input
            type="password"
            className="field"
            autoComplete="new-password"
            {...form.register("passwordConfirm")}
          />
        </Field>

        {serverError && <FormError>{serverError}</FormError>}

        <button
          type="submit"
          className="btn-primary w-full"
          disabled={reset.isPending || weakPassword}
        >
          {reset.isPending ? t("reset.saving") : t("reset.submit")}
        </button>
      </form>
    </AuthShell>
  );
}
